import { captureException, withScope } from "@sentry/vue";

export type AuthProviderId = "google.com" | "apple.com" | "microsoft.com";

type SanitizedAuthProviderError = {
  provider: AuthProviderId;
  firebaseCode: string;
  azureCode?: string;
  errorName: string;
};

const azureCodePattern = /\bAADSTS\d+\b/;
const nonActionableFirebaseCodes = new Set([
  "auth/cancelled-popup-request",
  "auth/popup-closed-by-user",
]);

const getStringProperty = (error: unknown, property: string) => {
  if (typeof error !== "object" || error === null) {
    return undefined;
  }

  const value = (error as Record<string, unknown>)[property];
  return typeof value === "string" ? value : undefined;
};

const trimTagValue = (value: string) => value.slice(0, 200);

export const getSanitizedAuthProviderError = (
  provider: AuthProviderId,
  error: unknown,
): SanitizedAuthProviderError => {
  const message = getStringProperty(error, "message") ?? "";
  const azureCode = message.match(azureCodePattern)?.[0];

  return {
    provider,
    firebaseCode: trimTagValue(getStringProperty(error, "code") ?? "unknown"),
    azureCode,
    errorName: trimTagValue(getStringProperty(error, "name") ?? "unknown"),
  };
};

export const shouldReportAuthProviderError = (error: unknown) => {
  const firebaseCode = getStringProperty(error, "code");
  return !firebaseCode || !nonActionableFirebaseCodes.has(firebaseCode);
};

export const reportAuthProviderError = (
  provider: AuthProviderId,
  error: unknown,
) => {
  if (!shouldReportAuthProviderError(error)) {
    return;
  }

  const details = getSanitizedAuthProviderError(provider, error);

  withScope((scope) => {
    scope.setTag("area", "auth");
    scope.setTag("auth.provider", details.provider);
    scope.setTag("auth.firebase_code", details.firebaseCode);
    scope.setTag("auth.error_name", details.errorName);

    if (details.azureCode) {
      scope.setTag("auth.azure_code", details.azureCode);
    }

    scope.setContext("auth_provider_error", details);
    scope.setFingerprint([
      "auth-provider-sign-in",
      details.provider,
      details.firebaseCode,
      details.azureCode ?? "no-provider-code",
    ]);
    captureException(new Error(`OAuth sign-in failed: ${details.provider}`));
  });
};
